import { type Locale } from '@syncanix/shared';
import { cn } from '@syncanix/ui';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useT } from '@/core/I18nProvider';
import { localizedPath } from '@/i18n/locale-path';

import { DOC_SECTIONS } from './content/docs-nav';
import { DOCS_SEARCH_QUERY_PARAM } from './docs-search-param';
import { buildDocSearchIndex, searchDocs, type DocSearchHit } from './search-docs';

import type { KeyboardEvent, ReactElement } from 'react';

const RESULT_LIMIT = 8;

/**
 * Instant docs search — an accessible combobox over the locale-resolved docs
 * registry (titles, headings, descriptions; see `search-docs.ts`). Fully
 * client-side: no network, no index download, works in all six locales.
 * `withSlashShortcut` lets ONE instance per page own the global `/` focus
 * shortcut (the desktop sidebar), so hidden duplicates never steal focus.
 */
export function DocsSearch({
  locale,
  withSlashShortcut = false,
  className,
}: {
  readonly locale: Locale;
  readonly withSlashShortcut?: boolean;
  readonly className?: string;
}): ReactElement {
  const { t } = useT();
  const navigate = useNavigate();
  const listboxId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  // Audit §14.2 — the query is URL-ADDRESSABLE, so `/docs?q=webhooks` lands on
  // results. That is what makes the site's declared schema.org `SearchAction`
  // truthful: declaring an endpoint that does not answer would publish a
  // capability that does not work, which is the failure this product exists to
  // prevent for customers and must not commit on its own site. The parameter
  // NAME is shared with that declaration (`docs-search-param.ts`) rather than
  // spelled twice, so the two cannot drift into a published lie.
  //
  // Seeded once from the initial URL rather than kept in sync on every
  // keystroke: the declaration only requires that the URL ANSWERS, and writing
  // a history entry per character would make Back unusable.
  const [searchParams] = useSearchParams();
  const initialQuery = (searchParams.get(DOCS_SEARCH_QUERY_PARAM) ?? '').trim();
  const [query, setQuery] = useState(initialQuery);
  const [open, setOpen] = useState(initialQuery !== '');
  const [activeIndex, setActiveIndex] = useState(0);

  const index = useMemo(() => buildDocSearchIndex(DOC_SECTIONS, t), [t]);
  const hits = useMemo(() => searchDocs(index, query, RESULT_LIMIT), [index, query]);
  const expanded = open && query.trim().length > 0;

  useEffect(() => {
    if (!withSlashShortcut) return;
    const onKeyDown = (event: globalThis.KeyboardEvent): void => {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (
        target !== null &&
        (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))
      ) {
        return;
      }
      event.preventDefault();
      inputRef.current?.focus();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [withSlashShortcut]);

  const goTo = (hit: DocSearchHit): void => {
    const anchor = hit.headingId !== undefined ? `#${hit.headingId}` : '';
    setQuery('');
    setOpen(false);
    inputRef.current?.blur();
    void navigate(`${localizedPath(`/docs/${hit.slug}`, locale)}${anchor}`);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (!expanded || hits.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((i) => (i + 1) % hits.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((i) => (i - 1 + hits.length) % hits.length);
    } else if (event.key === 'Enter') {
      const hit = hits[Math.min(activeIndex, hits.length - 1)];
      if (hit !== undefined) {
        event.preventDefault();
        goTo(hit);
      }
    }
  };

  return (
    <div role="search" className={cn('relative', className)}>
      <svg
        aria-hidden="true"
        viewBox="0 0 20 20"
        fill="none"
        className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--bd-faint)]"
      >
        <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.5" />
        <path d="m13.5 13.5 3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
      <input
        ref={inputRef}
        type="search"
        role="combobox"
        aria-label={t('docs.search.label')}
        aria-expanded={expanded}
        aria-controls={listboxId}
        aria-activedescendant={
          expanded && hits.length > 0 ? `${listboxId}-opt-${activeIndex}` : undefined
        }
        aria-autocomplete="list"
        aria-keyshortcuts={withSlashShortcut ? '/' : undefined}
        autoComplete="off"
        placeholder={t('docs.search.placeholder')}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setActiveIndex(0);
        }}
        onFocus={() => {
          setOpen(true);
        }}
        onBlur={() => {
          setOpen(false);
        }}
        onKeyDown={onKeyDown}
        className="w-full rounded-[11px] border border-[color:var(--bd-line-2)] bg-[color:var(--bd-page)] py-2 pe-9 ps-9 text-sm text-[color:var(--bd-ink)] transition-colors duration-200 placeholder:text-[color:var(--bd-faint)] hover:border-[color:var(--bd-line-3)] focus-visible:border-[color:var(--bd-blue)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:color-mix(in_srgb,var(--bd-blue)_25%,transparent)]"
      />
      {withSlashShortcut ? (
        <kbd
          aria-hidden="true"
          className="pointer-events-none absolute end-3 top-1/2 -translate-y-1/2 rounded border border-[color:var(--bd-line)] bg-[color:var(--bd-sunk)] px-1.5 font-mono text-xs text-[color:var(--bd-faint)]"
        >
          /
        </kbd>
      ) : null}

      <div aria-live="polite" className="sr-only">
        {expanded ? t('docs.search.resultCount', { count: hits.length }) : ''}
      </div>

      <div
        className={cn(
          'absolute inset-x-0 top-full z-30 mt-2 max-h-96 overflow-y-auto rounded-[14px] border border-[color:var(--bd-line-2)] bg-[color:var(--bd-page)] p-1.5',
          'shadow-[0_28px_60px_-24px_rgba(12,13,16,.34),0_4px_14px_-8px_rgba(12,13,16,.16)]',
          expanded ? 'block' : 'hidden',
        )}
      >
        <ul id={listboxId} role="listbox" aria-label={t('docs.search.resultsLabel')}>
          {hits.map((hit, i) => (
            <li
              key={`${hit.slug}#${hit.headingId ?? ''}`}
              id={`${listboxId}-opt-${i}`}
              role="option"
              aria-selected={i === activeIndex}
              // Fires before the input's blur, so selection wins the race.
              onMouseDown={(e) => {
                e.preventDefault();
                goTo(hit);
              }}
              onMouseEnter={() => {
                setActiveIndex(i);
              }}
              className={cn(
                'cursor-pointer rounded-[9px] px-3 py-2 transition-colors duration-150',
                // The same brand tint the sidebar's active row uses, so
                // "highlighted" means one thing across the docs shell.
                i === activeIndex ? 'bg-[color:var(--bd-tint)]' : undefined,
              )}
            >
              <span className="block font-mono rtl:font-sans text-[10px] uppercase tracking-[0.12em] text-[color:var(--bd-faint)] rtl:normal-case rtl:tracking-normal">
                {hit.sectionTitle}
              </span>
              <span className="mt-0.5 block text-sm font-medium tracking-[-0.015em] text-[color:var(--bd-ink)]">
                {hit.title}
              </span>
              {hit.headingText !== undefined ? (
                <span className="mt-1 block border-s-2 border-[color:var(--bd-dot)] ps-2 text-xs text-[color:var(--bd-muted)]">
                  {hit.headingText}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
        {hits.length === 0 ? (
          <p className="px-3 py-2 text-sm text-[color:var(--bd-faint)]">
            {t('docs.search.noResults', { query: query.trim() })}
          </p>
        ) : null}
      </div>
    </div>
  );
}
